import { useState, useEffect } from 'react'
import { X, Download, Smartphone } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true)
      return
    }

    // Check visit count for mobile devices
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
    if (!isMobile) {
      return
    }

    const visitCount = parseInt(localStorage.getItem('pwa-visit-count') || '0', 10)
    const promptDismissed = localStorage.getItem('pwa-prompt-dismissed') === 'true'
    const lastPromptTime = parseInt(localStorage.getItem('pwa-last-prompt-time') || '0', 10)
    const daysSinceLastPrompt = (Date.now() - lastPromptTime) / (1000 * 60 * 60 * 24)

    // Increment visit count
    localStorage.setItem('pwa-visit-count', (visitCount + 1).toString())

    // Show prompt after 2 visits, and respect dismissal for 7 days
    const shouldShowPrompt = visitCount >= 1 && (!promptDismissed || daysSinceLastPrompt > 7)

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      const promptEvent = e as BeforeInstallPromptEvent
      setDeferredPrompt(promptEvent)

      if (shouldShowPrompt) {
        setTimeout(() => {
          setShowPrompt(true)
        }, 3000) // Show after 3 seconds
      }
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('PWA installed successfully')
      setIsInstalled(true)
      setShowPrompt(false)
      localStorage.removeItem('pwa-visit-count')
      localStorage.removeItem('pwa-prompt-dismissed')

      // Track installation event (optional analytics)
      if (window.gtag) {
        window.gtag('event', 'pwa_install', {
          event_category: 'engagement',
          event_label: 'PWA Installation'
        })
      }
    }

    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return
    }

    // Show the install prompt
    await deferredPrompt.prompt()

    // Wait for user response
    const { outcome } = await deferredPrompt.userChoice
    console.log(`User ${outcome} the install prompt`)

    if (outcome === 'accepted') {
      localStorage.setItem('pwa-last-prompt-time', Date.now().toString())
    }

    // Clear the deferred prompt
    setDeferredPrompt(null)
    setShowPrompt(false)

    // Track user choice
    if (window.gtag) {
      window.gtag('event', 'pwa_prompt_response', {
        event_category: 'engagement',
        event_label: outcome
      })
    }
  }

  const handleDismiss = () => {
    setShowPrompt(false)
    localStorage.setItem('pwa-prompt-dismissed', 'true')
    localStorage.setItem('pwa-last-prompt-time', Date.now().toString())

    // Track dismissal
    if (window.gtag) {
      window.gtag('event', 'pwa_prompt_dismissed', {
        event_category: 'engagement'
      })
    }
  }

  if (isInstalled || !showPrompt || !deferredPrompt) {
    return null
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 text-white">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
                <Smartphone className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">Installa CodiceCivile.AI</h3>
                <p className="text-sm text-blue-100 mt-0.5">Accesso rapido e offline</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <ul className="space-y-2 mb-4 text-sm text-gray-600 dark:text-gray-300">
            <li className="flex items-center gap-2">
              <span className="text-green-500 font-bold">✓</span>
              <span>Accesso istantaneo dalla schermata home</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500 font-bold">✓</span>
              <span>Funziona offline con conversazioni salvate</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-green-500 font-bold">✓</span>
              <span>Esperienza app nativa a schermo intero</span>
            </li>
          </ul>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleInstallClick}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
            >
              <Download className="w-4 h-4" />
              <span>Installa App</span>
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-3 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors font-medium"
            >
              Dopo
            </button>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 text-center">
            Non richiede spazio di archiviazione aggiuntivo
          </p>
        </div>
      </div>
    </div>
  )
}

export default PWAInstallPrompt
